'use client';

import Image from 'next/image';
import type { ImgHTMLAttributes } from 'react';
import { isDirectCdnImageUrl, isHttpOrHttpsImageUrl, isUrlSafeForNextImage } from '@/lib/remoteImageAllowlist';
import { cn } from '@/lib/utils';

type Base = {
  src: string;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  onError?: ImgHTMLAttributes<HTMLImageElement>['onError'];
};

export type SafeRemoteImageProps =
  | (Base & { fill: true; width?: never; height?: never })
  | (Base & { fill?: false; width: number; height: number });

export function SafeRemoteImage(props: SafeRemoteImageProps) {
  const { src, alt, className, sizes, priority, onError } = props;
  const trimmed = src.trim();
  if (!trimmed) return null;

  const fill = props.fill === true;
  const useDirectImage = isDirectCdnImageUrl(trimmed);

  if (trimmed.startsWith('data:')) {
    if (fill) {
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={trimmed}
          alt={alt}
          className={cn('absolute inset-0 h-full w-full object-cover', className)}
          onError={onError}
        />
      );
    }
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={trimmed}
        alt={alt}
        width={props.width}
        height={props.height}
        className={className}
        onError={onError}
      />
    );
  }

  if (useDirectImage) {
    if (fill) {
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={trimmed}
          alt={alt}
          sizes={sizes}
          className={cn('absolute inset-0 h-full w-full object-cover', className)}
          loading={priority ? 'eager' : 'lazy'}
          referrerPolicy="no-referrer"
          onError={onError}
        />
      );
    }
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={trimmed}
        alt={alt}
        width={props.width}
        height={props.height}
        className={className}
        loading={priority ? 'eager' : 'lazy'}
        referrerPolicy="no-referrer"
        onError={onError}
      />
    );
  }

  if (isUrlSafeForNextImage(trimmed)) {
    if (fill) {
      return (
        <Image
          src={trimmed}
          alt={alt}
          fill
          sizes={sizes}
          className={className}
          priority={priority}
          onError={onError}
        />
      );
    }
    return (
      <Image
        src={trimmed}
        alt={alt}
        width={props.width}
        height={props.height}
        sizes={sizes}
        className={className}
        priority={priority}
        onError={onError}
      />
    );
  }

  if (!isHttpOrHttpsImageUrl(trimmed)) {
    return null;
  }

  if (fill) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={trimmed}
        alt={alt}
        sizes={sizes}
        className={cn('absolute inset-0 h-full w-full object-cover', className)}
        loading={priority ? 'eager' : 'lazy'}
        referrerPolicy="no-referrer"
        onError={onError}
      />
    );
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={trimmed}
      alt={alt}
      width={props.width}
      height={props.height}
      className={className}
      loading={priority ? 'eager' : 'lazy'}
      referrerPolicy="no-referrer"
      onError={onError}
    />
  );
}
